// @ts-strict-ignore
import React from 'react'
import { Linking } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { connect } from 'react-redux'
import Icon from 'react-native-vector-icons/MaterialIcons'
import Alert from 'APP/Converse/Alert'
import PatientActions from 'APP/Redux/PatientRedux'

import Config from 'APP/Config'

// services
import I18n from 'APP/I18n'
import UrlBuilder from 'APP/Helpers/UrlBuilder'
import Analytics from 'APP/Services/Analytics'

import { getDisplayableBrands, openExternalBrandApp } from 'APP/Lib/BrandHelpers'

//components
import InfoCard from 'APP/Components/InfoCard'

// Styling
import { logger } from 'APP/Lib/Datadog'
import Typography from 'APP/Converse/Typography'
import styled from 'styled-components/native'
import { useTheme } from 'styled-components/native'
import { Dispatch } from 'redux'
import { RootState } from 'APP/Store/CreateStore'

const StyledTopSubtitle = styled(Typography).attrs(() => ({
  variant: 'bodyNormal',
}))`
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
`

const StyledTitle = styled(Typography).attrs(() => ({
  variant: 'subheaderBold',
}))`
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 0.75}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.regular}px;
`

const StyledInfoCard = styled(InfoCard)`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

type InactiveInfoCardProps = {
  eligibleBrands: string[]
  crossBrandBrands?: string[]
  busy: boolean
  unchallengedInvitationSearch: () => void
  testId: string
}

const _InactiveInfoCard = ({
  eligibleBrands,
  crossBrandBrands,
  busy,
  unchallengedInvitationSearch,
  testId,
}: InactiveInfoCardProps) => {
  const analyticsName = 'Inactive info card'
  const navigation = useNavigation()
  const filteredBrands = getDisplayableBrands(eligibleBrands, crossBrandBrands)
  const theme = useTheme()

  const handleOnLinkPressed = () => {
    Analytics.trackEvent(
      'button_click',
      {
        button_text: 'Contact us',
        button_value: `${analyticsName}`,
      },
      ['banner_context']
    )

    if (Config.CUSTOM_SUPPORT_PAGE) {
      navigation.navigate('contactUs')
    } else {
      const url = UrlBuilder.supportEmailUrl()
      if (Linking.canOpenURL(url)) {
        Linking.openURL(url).catch((e) => {
          logger.error('[InactiveCard] Error opening contact us link', { url }, e)
          Alert.alert(I18n.t('CannotOpenEmail.title'), I18n.t('CannotOpenEmail.body'), [
            { text: I18n.t('CannotOpenEmail.ok') },
          ])
        })
      }
    }
  }

  // Current eligibility is deactivated, but has an active eligibility in other brand
  if (filteredBrands?.length > 0) {
    return filteredBrands.map((brand: string) => {
      const appName = I18n.t(`InactiveInfoCard.multiBrand.${brand}.title`)
      const broughtBy = I18n.t(`InactiveInfoCard.multiBrand.${brand}.body`)
      const customSubtitle = (
        <StyledTopSubtitle>
          <StyledTitle>{appName}</StyledTitle>
          {
            `, ${broughtBy}.` // eslint-disable-line react-native/no-raw-text
          }
        </StyledTopSubtitle>
      )
      return (
        <StyledInfoCard
          key={brand}
          title={I18n.t('InactiveInfoCard.multiBrand.title')}
          customSubtitle={customSubtitle}
          buttonText={I18n.t('InactiveInfoCard.multiBrand.cta')}
          onButtonPressed={() => openExternalBrandApp(appName, brand)}
          icon={
            <Icon
              name="exit-to-app"
              size={40}
              color={theme.colors.accent}
              accessibilityRole="image"
            />
          }
          buttonAnalyticsName={analyticsName}
          linkText={I18n.t('Common.somethingWrong')}
          linkCta={I18n.t('Common.contactUs')}
          onLinkPressed={handleOnLinkPressed}
          busy={busy}
          testId={testId}
        />
      )
    })
  }
  return (
    <InfoCard
      title={I18n.t('InactiveInfoCard.singleBrand.title')}
      subtitle={I18n.t('InactiveInfoCard.singleBrand.subtitle')}
      buttonText={I18n.t('InactiveInfoCard.singleBrand.cta')}
      onButtonPressed={unchallengedInvitationSearch}
      icon={
        <Icon name="lock-outline" size={40} color={theme.colors.accent} accessibilityRole="image" />
      }
      buttonAnalyticsName={analyticsName}
      linkText={I18n.t('Common.somethingWrong')}
      linkCta={I18n.t('Common.contactUs')}
      onLinkPressed={handleOnLinkPressed}
      busy={busy}
      testId={testId}
    />
  )
}

const mapStateToProps = ({ patient, features }: RootState) => {
  return {
    eligibleBrands: patient?.profile?.eligibleBrands,
    crossBrandBrands: features?.crossBrandEligibilityBrands,
    busy:
      (patient?.unchallengedInvitationSearchRunning ?? false) ||
      (patient?.challengedInvitationSearchRunning ?? false),
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    unchallengedInvitationSearch: () =>
      dispatch(PatientActions.unchallengedInvitationSearchRequest()),
  }
}

const InactiveInfoCard = connect(mapStateToProps, mapDispatchToProps)(_InactiveInfoCard)

export default InactiveInfoCard
